import { useCallback, useEffect, useState } from 'react';
import { holidays as holidaysApi, stores as storesApi, settings as settingsApi } from '../api/endpoints';
import { useToast } from '../context/ToastContext';
import { Loading, Empty, Modal, Can, dateOnly } from '../components/Common';

const WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

const TYPE_LABEL = {
  closed: 'Fully closed',
  'no-pickup': 'No pickups',
  'no-delivery': 'No deliveries',
};

/**
 * Holidays & schedule.
 *
 * Anything blocked here disappears from the date picker in the customer app and
 * is rejected server-side if an order tries to use it anyway, so a stale app
 * build can never book into a closed day.
 */
export default function Holidays() {
  const toast = useToast();

  const [list, setList] = useState([]);
  const [preview, setPreview] = useState([]);
  const [config, setConfig] = useState(null);
  const [storeList, setStoreList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    try {
      const [holidayList, calendar, settingsRes, stores] = await Promise.all([
        holidaysApi.list({ upcoming: 'true' }),
        holidaysApi.calendar(),
        settingsApi.get(),
        storesApi.list({ limit: 100 }).catch(() => ({ data: { stores: [] } })),
      ]);
      setList(holidayList.data.holidays);
      setPreview(calendar.data.days);
      setConfig(settingsRes.data.settings);
      setStoreList(stores.data.stores);
    } catch (err) {
      toast.error('Could not load the schedule', err.message);
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { document.title = 'Holidays & Schedule · UrbanClean'; }, []);

  const remove = async (holiday) => {
    if (!window.confirm(`Remove "${holiday.title}"?`)) return;
    try {
      await holidaysApi.remove(holiday._id);
      toast.success('Holiday removed');
      load();
    } catch (err) {
      toast.error('Could not remove', err.message);
    }
  };

  const saveSchedule = async (patch) => {
    try {
      await settingsApi.update(patch);
      toast.success('Schedule updated');
      load();
    } catch (err) {
      toast.error('Could not save', err.message);
    }
  };

  if (loading) return <Loading label="Loading the schedule" />;

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Holidays &amp; Schedule</h1>
          <div className="sub">Closed days disappear from the app's booking calendar</div>
        </div>
        <Can permission="holiday.create">
          <button className="btn btn-blue" onClick={() => setEditing({})}>+ Add holiday</button>
        </Can>
      </div>

      {/* Live preview of exactly what the app will show */}
      <div className="card mb">
        <div className="card-head">
          <h3>What customers see right now</h3>
          <span className="muted" style={{ fontSize: 12 }}>
            Next {preview.length} day(s)
          </span>
        </div>
        <div className="card-body">
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            {preview.map((day) => (
              <div
                key={day.date}
                title={day.reason || 'Available'}
                style={{
                  minWidth: 92, padding: '11px 13px', borderRadius: 10, textAlign: 'center',
                  border: `1.5px solid ${day.available ? 'var(--blue)' : 'var(--line)'}`,
                  background: day.available ? 'var(--blue-50)' : 'var(--bg)',
                  opacity: day.available ? 1 : 0.75,
                }}
              >
                <div className="strong" style={{ fontSize: 13, color: day.available ? 'var(--navy)' : 'var(--muted)' }}>
                  {day.label}
                </div>
                <div className="muted" style={{ fontSize: 10.5, marginTop: 2 }}>{day.date.slice(5)}</div>
                <div style={{ marginTop: 6 }}>
                  {day.available ? (
                    <span className="badge badge-green">Open</span>
                  ) : (
                    <span className="badge badge-red">{day.reason}</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="grid grid-2 mb">
        {/* Recurring schedule */}
        <div className="card">
          <div className="card-head"><h3>Weekly schedule</h3></div>
          <div className="card-body">
            <div className="field">
              <label>Weekly off days</label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {WEEKDAYS.map((day, index) => {
                  const on = (config?.weeklyOffDays || []).includes(index);
                  return (
                    <label key={day} className={`perm-chip ${on ? 'on' : ''}`}>
                      <input
                        type="checkbox"
                        checked={on}
                        onChange={() => {
                          const current = config?.weeklyOffDays || [];
                          const next = on ? current.filter((d) => d !== index) : [...current, index];
                          setConfig((c) => ({ ...c, weeklyOffDays: next }));
                          saveSchedule({ weeklyOffDays: next });
                        }}
                      />
                      {on ? '✓' : '＋'} {day.slice(0, 3)}
                    </label>
                  );
                })}
              </div>
              <div className="hint">Recurring closures. One-off dates go in the holiday list below.</div>
            </div>

            <div className="row">
              <div className="field">
                <label>Booking window (days)</label>
                <input
                  className="input"
                  type="number"
                  value={config?.bookingWindowDays ?? 7}
                  onChange={(e) => setConfig((c) => ({ ...c, bookingWindowDays: e.target.value }))}
                  onBlur={(e) => saveSchedule({ bookingWindowDays: Number(e.target.value) })}
                />
                <div className="hint">How far ahead a customer can book.</div>
              </div>
              <div className="field">
                <label>Same-day cutoff hour</label>
                <input
                  className="input"
                  type="number"
                  min={0}
                  max={23}
                  value={config?.sameDayCutoffHour ?? 18}
                  onChange={(e) => setConfig((c) => ({ ...c, sameDayCutoffHour: e.target.value }))}
                  onBlur={(e) => saveSchedule({ sameDayCutoffHour: Number(e.target.value) })}
                />
                <div className="hint">After this hour, today is no longer bookable (24-hour clock).</div>
              </div>
            </div>
          </div>
        </div>

        {/* Slots */}
        <div className="card">
          <div className="card-head"><h3>Pickup time slots</h3></div>
          <div className="card-body">
            <div className="field">
              <label>Slots (one per line)</label>
              <textarea
                className="textarea"
                style={{ minHeight: 168 }}
                value={(config?.pickupSlots || []).join('\n')}
                onChange={(e) => setConfig((c) => ({ ...c, pickupSlots: e.target.value.split('\n') }))}
              />
              <div className="hint">
                Shown on the booking screen. An order using a slot that is no longer listed is rejected.
              </div>
            </div>
            <Can permission="settings.update">
              <button
                className="btn btn-primary"
                onClick={() => saveSchedule({
                  pickupSlots: (config?.pickupSlots || []).map((s) => s.trim()).filter(Boolean),
                })}
              >
                Save slots
              </button>
            </Can>
          </div>
        </div>
      </div>

      {/* Holiday list */}
      <div className="card">
        <div className="card-head"><h3>Upcoming holidays</h3></div>
        {list.length === 0 ? (
          <Empty title="No holidays set" body="Add the dates you'll be closed and they'll drop out of the app calendar." />
        ) : (
          <div className="table-wrap">
            <table className="data">
              <thead>
                <tr><th>Date</th><th>Occasion</th><th>Type</th><th>Applies to</th><th>Status</th><th></th></tr>
              </thead>
              <tbody>
                {list.map((h) => (
                  <tr key={h._id}>
                    <td className="strong nowrap">{dateOnly(h.date)}</td>
                    <td>
                      {h.title}
                      {h.description ? <div className="muted" style={{ fontSize: 12 }}>{h.description}</div> : null}
                    </td>
                    <td>
                      <span className={`badge badge-${h.type === 'closed' ? 'red' : 'amber'}`}>
                        {TYPE_LABEL[h.type] || h.type}
                      </span>
                    </td>
                    <td className="muted">
                      {h.stores?.length ? h.stores.map((s) => s.name).join(', ') : 'All stores'}
                    </td>
                    <td>
                      <span className={`badge badge-${h.isActive ? 'green' : 'gray'}`}>
                        {h.isActive ? 'Active' : 'Off'}
                      </span>
                    </td>
                    <td className="right nowrap">
                      <Can permission="holiday.update">
                        <button className="btn btn-sm btn-ghost" onClick={() => setEditing(h)}>Edit</button>
                      </Can>
                      <Can permission="holiday.delete">
                        <button className="btn btn-sm btn-ghost" style={{ marginLeft: 6, color: 'var(--red)' }} onClick={() => remove(h)}>
                          Delete
                        </button>
                      </Can>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <HolidayEditor
        holiday={editing}
        storeList={storeList}
        onClose={() => setEditing(null)}
        onSaved={() => { setEditing(null); load(); }}
      />
    </>
  );
}

function HolidayEditor({ holiday, storeList, onClose, onSaved }) {
  const toast = useToast();
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!holiday) return;
    setForm({
      date: holiday.date ? String(holiday.date).slice(0, 10) : '',
      title: holiday.title || '',
      description: holiday.description || '',
      type: holiday.type || 'closed',
      stores: (holiday.stores || []).map((s) => s._id || s),
      isActive: holiday.isActive !== false,
    });
  }, [holiday]);

  if (!holiday) return null;
  const set = (key) => (e) =>
    setForm((f) => ({ ...f, [key]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }));

  const save = async () => {
    if (!form.date || !form.title) return toast.error('A date and a name are required');
    setBusy(true);
    try {
      if (holiday._id) await holidaysApi.update(holiday._id, form);
      else await holidaysApi.create(form);
      toast.success('Holiday saved');
      onSaved();
    } catch (err) {
      toast.error('Could not save', err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal open title={holiday._id ? `Edit — ${holiday.title}` : 'Add a holiday'} onClose={onClose}
      footer={<>
        <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
        <button className="btn btn-primary" onClick={save} disabled={busy}>{busy ? 'Saving…' : 'Save holiday'}</button>
      </>}>
      <div className="row">
        <div className="field"><label>Date *</label><input className="input" type="date" value={form.date || ''} onChange={set('date')} /></div>
        <div className="field"><label>Occasion *</label><input className="input" value={form.title || ''} onChange={set('title')} placeholder="Diwali" /></div>
      </div>

      <div className="field">
        <label>What is closed</label>
        <select className="select" value={form.type} onChange={set('type')}>
          <option value="closed">Fully closed — no pickups or deliveries</option>
          <option value="no-pickup">No pickups — deliveries still run</option>
          <option value="no-delivery">No deliveries — pickups still run</option>
        </select>
      </div>

      <div className="field">
        <label>Note (internal)</label>
        <input className="input" value={form.description || ''} onChange={set('description')} />
      </div>

      {storeList.length ? (
        <div className="field">
          <label>Applies to</label>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {storeList.map((s) => {
              const on = form.stores?.includes(s._id);
              return (
                <label key={s._id} className={`perm-chip ${on ? 'on' : ''}`}>
                  <input
                    type="checkbox"
                    checked={on}
                    onChange={() => setForm((f) => ({
                      ...f,
                      stores: on ? f.stores.filter((id) => id !== s._id) : [...(f.stores || []), s._id],
                    }))}
                  />
                  {on ? '✓' : '＋'} {s.name}
                </label>
              );
            })}
          </div>
          <div className="hint">Leave all unticked to close every store on this date.</div>
        </div>
      ) : null}

      <div className="field">
        <label className="switch">
          <input type="checkbox" checked={form.isActive} onChange={set('isActive')} />
          <span className="track" /><span>Active</span>
        </label>
      </div>
    </Modal>
  );
}
